import { Mail, Phone } from "lucide-react";
import { MaskLines, Reveal, Slug } from "@/components/minka/Motion";
import { Seo } from "@/components/minka/Seo";
import { InquiryForm } from "@/components/minka/InquiryForm";

const TIERS = [
  { n: "01", jp: "選択", t: "Option Agreement", d: "Secure exclusive rights to develop MINKA for a set period, with the right to purchase." },
  { n: "02", jp: "購入", t: "Outright Purchase", d: "Acquire the screenplay and its film rights outright." },
  { n: "03", jp: "仲介", t: "Shopping Agreement", d: "For producers who want to take MINKA to studios, financiers or talent." },
  { n: "04", jp: "脚本", t: "Request the Script", d: "Producers, agents and executives may request the full screenplay to read." },
];

const LOCATIONS = [
  { n: "01", place: "New Mexico", jp: "米国", lines: [["Interior scenes", "Santa Fe Studio"], ["Exterior scenes", "Ruidoso or Red River"]] },
  { n: "02", place: "Canada", jp: "加奈陀", lines: [] },
  { n: "03", place: "Japan", jp: "日本", lines: [["Locations", "Morioka city and / or countryside"]] },
];

const Locations = () => (
  <section className="mt-28" data-testid="filming-locations">
    <Reveal><Slug>Proposed filming locations · ロケ地</Slug></Reveal>
    <div className="mt-8 border-t border-white/10">
      {LOCATIONS.map((l, i) => (
        <Reveal key={l.n} delay={i * 0.08}>
          <div data-testid={`location-${l.n}`} className="group grid gap-4 border-b border-white/10 bg-[#07080B]/60 px-2 py-8 backdrop-blur-sm transition-colors duration-500 hover:bg-[#0E1017]/90 md:grid-cols-[80px_1fr_1.4fr] md:items-baseline md:px-6">
            <span className="font-mono-s text-xs text-[#A8DADC]">{l.n}</span>
            <h3 className="flex items-baseline gap-4 font-display text-3xl font-bold text-[#E2E0D8] transition-transform duration-500 group-hover:translate-x-2 md:text-5xl">
              {l.place}<span className="text-lg text-[#C81D25]/80 md:text-2xl">{l.jp}</span>
            </h3>
            <div className="space-y-2">
              {l.lines.map(([k, v]) => (
                <p key={k} className="text-base text-[#C3CAD5] md:text-lg">
                  <span className="mr-3 font-mono-s text-xs uppercase tracking-[0.2em] text-[#E76F51]">{k}</span>{v}
                </p>
              ))}
            </div>
          </div>
        </Reveal>
      ))}
    </div>
  </section>
);

const Tier = ({ x, i }) => (
  <Reveal delay={i * 0.08} className="group relative overflow-hidden border border-white/10 bg-[#0E1017]/85 p-8 backdrop-blur-md transition-colors duration-500 hover:border-[#C81D25]/60" >
    <div data-testid={`option-tier-${x.n}`}>
      <div className="flex items-baseline justify-between">
        <span className="font-mono-s text-xs text-[#A8DADC]">{x.n}</span>
        <span className="font-display text-2xl text-[#C81D25]/80 transition-transform duration-500 group-hover:-translate-y-1">{x.jp}</span>
      </div>
      <h3 className="mt-10 font-display text-2xl font-bold text-[#E2E0D8]">{x.t}</h3>
      <p className="mt-3 text-sm leading-relaxed text-[#C3CAD5] md:text-base">{x.d}</p>
    </div>
  </Reveal>
);

const Contact = () => (
  <Reveal className="border border-white/10 bg-[#07080B]/80 p-8 backdrop-blur-md">
    <Slug>Direct contact</Slug>
    <p className="mt-4 font-serif-i text-2xl text-[#E2E0D8]">Nicola Davis</p>
    <div className="mt-8 space-y-4">
      <a href="mailto:nini.laughlin@gmail.com" data-testid="contact-email-link" className="group flex items-center gap-4 text-[#E2E0D8]">
        <Mail size={18} className="text-[#C81D25]" /><span className="link-underline break-all">nini.laughlin@gmail.com</span>
      </a>
      <a href="tel:5056600596" data-testid="contact-phone-link" className="group flex items-center gap-4 text-[#E2E0D8]">
        <Phone size={18} className="text-[#C81D25]" /><span className="link-underline">505-660-0596</span>
      </a>
    </div>
  </Reveal>
);

export default function Options() {
  return (
    <div className="px-5 pb-32 pt-32 md:px-10 md:pt-40" data-testid="options-page">
      <Seo
        title="Film Options & Rights — MINKA Screenplay"
        description="Option, purchase or shop MINKA, a supernatural horror screenplay by Nicola Davis. Every inquiry goes directly to the writer."
        path="/film-options"
      />
      <div className="mx-auto max-w-[1400px]">
        <MaskLines className="font-mono-s text-xs uppercase tracking-[0.35em] text-[#A8DADC]" lines={["Film options & rights · 映画化権"]} />
        <h1 className="mt-6 font-display text-5xl font-extrabold leading-[0.95] text-[#E2E0D8] sm:text-6xl lg:text-8xl">
          <MaskLines lines={["Bring MINKA", "to the screen."]} delay={0.2} lineClass="[&>em]:font-serif-i" />
        </h1>
        <Reveal delay={0.5}>
          <p className="mt-8 max-w-2xl text-base leading-relaxed text-[#C3CAD5] md:text-lg">
            The feature screenplay is available for option or purchase. Choose how you'd like to engage and send an inquiry — every message goes directly to the writer.
          </p>
        </Reveal>
        <div className="mt-20 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {TIERS.map((x, i) => <Tier key={x.n} x={x} i={i} />)}
        </div>
        <Locations />
        <div className="mt-24 grid gap-10 lg:grid-cols-[7fr_4fr] lg:items-start">
          <InquiryForm />
          <div className="lg:sticky lg:top-28"><Contact /></div>
        </div>
      </div>
    </div>
  );
}
